import BreadCrumb from "../../../components/BreadCrumb";
import Layout from "../../../components/Layout";
import { Grid } from "@mui/material";
import InputGroup from "../../../components/FormComponent/InputGroup";
import ReactQuill from "react-quill";
import { Priority, SupportDepartment } from "../../../utils/DropDown";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { httprequest } from "../../../data/api";
import { useNavigate } from "react-router-dom";
// import "react-quill/dist/quill.snow.css";
import { useState, useEffect } from "react";
import Button from "../../../components/Button";
import useStyles from "../styles";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import useValidator from "../../../hooks/useValidator";

const UPLOAD_ENDPOINT = "/api/v1/upload";
const CREATE_TICKET_ENDPOINT = "/api/v1/create-ticket";

const CreateTicket = () => {
  // const [noteValue, setNoteValue] = useState("");
  const classes = useStyles();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [ticketValidator, showTicketValidator] = useValidator();
  const [uploadForm, setUploadForm] = useState({});
  const [selectedFile, setSelectedFile] = useState("");
  const [ticketForm, setTicketForm] = useState({
    department: "",
    priority: "",
    status: "open",
    subject: "",
    message: "",
    attachment: {}
  });

  /**
 * Handles changes in regular form inputs.
 * @function
 * @param {Object} e - The event object from the input element.
 * @description Updates the ticketForm state with the changed input value.
 */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTicketForm({ ...ticketForm, [name]: value });
  };
  /**
 * Handles changes in the Quill rich text editor input.
 * @function
 * @param {string} e - The HTML content from the Quill editor.
 * @description Converts HTML to plain text and updates the message in ticketForm state.
 */
  const handleQuillChange = (e) => {
    const plainText = e.replace(/<[^>]+>/g, "");
    setTicketForm({ ...ticketForm, message: plainText });
  };

  /**
 * Handles ticket creation including file upload and form submission.
 * @async
 * @returns {Promise<void>}
 * @description Validates form, handles file upload if present, submits ticket data,
 * and resets form on success. Shows appropriate toast notifications.
 */
  const handleCreteTicket = async () => {
    if (!ticketValidator.allValid()) {
      showTicketValidator(true);

      return;
    }

    setIsLoading(true);

    try {
      let uploadData = {};

      // Handle file upload if there's a selected file
      if (selectedFile) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        const response = await httprequest.post(`${UPLOAD_ENDPOINT}`, formData);

        if (response.status === 200 && response?.data?.data) {
          const { upload_id, file_url } = response.data.data[0];
          uploadData = { attachment: { upload_id, file_url } };
        }
      }

      // ready final data object for post
      const data = { ...ticketForm, ...uploadData };

      // Submit the notice data
      const noticeResponse = await httprequest.post(CREATE_TICKET_ENDPOINT, data);

      toast.success(noticeResponse?.data?.message);

      // Reset form state
      setTicketForm({
        department: "",
        priority: "",
        status: "",
        subject: "",
        message: "",
        attachment: {}
      });
      setSelectedFile("");
      navigate("/support/tickets");
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
 * Sets up Quill editor styles on component mount.
 * @effect
 * @description Applies custom styles to Quill editor container and toolbar.
 */
  useEffect(() => {
    const qlContainer = document.querySelector(".ql-container");
    if (qlContainer) {
      qlContainer.style.minHeight = "140px";
      qlContainer.style.borderRadius = "0px 0px 10px 10px";
    }

    const qlToolbar = document.querySelector(".ql-toolbar.ql-snow");
    if (qlToolbar) {
      qlToolbar.style.borderRadius = "10px 10px 0px 0px";
    }
  }, []);

  /**
 * Handles file selection for ticket attachments.
 * @function
 * @param {Object} e - The event object from the file input.
 * @description Updates selectedFile state and formats filename in uploadForm.
 */
  const handleFileChange = (e) => {
    // const { name } = e.target;
    if (e.target.files) setSelectedFile(e.target.files[0]);
    setUploadForm({
      ...uploadForm,
      file: e.target.files[0].name.replaceAll(" ", "-")
    });
  };
  /**
 * Removes the selected file from the ticket.
 * @function
 * @description Clears the selectedFile state.
 */
  const handleRemoveFile = () => setSelectedFile("");
  /**
 * Prevents file drops in the editor.
 * @function
 * @param {Object} event - The drop event object.
 * @description Stops propagation of file drop events in the editor.
 */
  const handleEditorDrop = (event) => {
    if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      // Prevent drop if it's a file (e.g., image)
      event.preventDefault();
      event.stopPropagation();
    }
  };
  /**
 * Prevents image pasting in the editor.
 * @function
 * @param {Object} event - The paste event object.
 * @description Checks clipboard items and prevents pasting if images are detected.
 */
  const handlePaste = (event) => {
    const items = event.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      // Check if any pasted item is an image
      if (items[i].type.indexOf("image") !== -1) {
        event.preventDefault(); // Prevent the paste

        return;
      }
    }
  };

  /**
 * Allowed formats for the Quill rich text editor.
 * @type {Array<string>}
 * @description Explicitly defines allowed formats to prevent image/video embedding.
 * Includes text formatting options but excludes 'image' and 'video'.
 */
  const Quillformats = ["background", "bold", "color", "font", "code", "italic", "link", "size",
    "strike", "script", "underline", "blockquote", "header", "indent", "list", "align", "direction", "code-block", "formula"
    // 'image'
    // 'video'
  ];

  return (
    <Layout
      children={
        <>
          <div className="breadcrumb-container">
            <div>
              <BreadCrumb
                link_one="/support/tickets"
                link_one_name="Tickets"
                active="Open Ticket"
                description="View all your tickets here"
              />
            </div>
          </div>
          <div className="page-content text-left-important">
            <Grid container spacing={2}>
              {/* <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup required label="Email Address" onChange={handleInputChange} inputType="text" inputName="email" />
                {ticketValidator.message("Email", ticketForm?.email, "required")}
              </Grid> */}
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <SelectGroup
                  label="Department"
                  required
                  children={
                    <select onChange={handleInputChange} name="department">
                      <option value="">--- Select ---</option>
                      {SupportDepartment?.map((dept) => {
                        return (
                          <option key={dept?.text} value={dept?.key}>
                            {dept?.text}
                          </option>
                        );
                      })}
                    </select>
                  }
                />
                {ticketValidator.message("Department", ticketForm?.department, "required")}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <SelectGroup
                  label="Priority"
                  required
                  children={
                    <select onChange={handleInputChange} name="priority">
                      <option value="">--- Select ---</option>
                      {Priority?.map((priority) => {
                        return (
                          <option key={priority?.text} value={priority?.key}>
                            {priority?.text}
                          </option>
                        );
                      })}
                    </select>
                  }
                />
                {ticketValidator.message("Priority", ticketForm?.priority, "required")}
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <InputGroup onChange={handleInputChange} required label="Subject" inputType="text" inputName="subject" placeHolder="Ticket Subject" />
                {ticketValidator.message("Subject", ticketForm?.subject, "required")}
              </Grid>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <div className={`${classes.quillcreateposition} margin-top-16 margin-bottom-14`}>
                <p className="text-align-left no-margin-block">
                  <strong>Message</strong>
                  <span className="input-required">*</span>
                </p>
                <ReactQuill
                  theme="snow"
                  onDrop={handleEditorDrop}
                  onChange={handleQuillChange}
                  onPaste={handlePaste}
                  formats={Quillformats}
                  modules={{
                    toolbar: [
                      [{ "header": [1, 2, false] }],
                      ["bold", "italic", "underline"],
                      [{ "list": "ordered" }, { "list": "bullet" }],
                      ["clean"] // Remove the image button
                    ]
                  }}
                  // value={noteValue}
                  style={{
                    backgroundColor: "#F9FAFB",
                    minHeight: "180px",
                    borderRadius: 20,
                    textAlign: "left",
                    "& qlContainer": { height: "150px" }
                  }}
                />
                {ticketValidator.message("Message", ticketForm?.message, "required")}
              </div>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <p className="text-align-left no-margin-block">
                <strong>Additional information</strong>
                {/* <span className="input-required">*</span> */}
              </p>
              <div className={classes.pickercontainer}>
                <input
                  type="file"
                  required
                  className={classes.picker}
                  onChange={handleFileChange}
                  name="file"
                  accept=".png, .jpg"
                />

                <div className={classes.uploadflex}>
                  <div>
                    <Icon
                      icon="icon-park-outline:upload-two"
                      className={classes.uploadicon}
                    />
                  </div>
                  {selectedFile?.name ? (
                    <div>
                      <h2 className={classes.uploadtitle}>
                        {selectedFile?.name}
                      </h2>
                    </div>
                  ) : (
                    <div>
                      <p className={classes.uploadtitle}>
                        Tap to Upload
                      </p>
                      <p className={classes.uploadsubtitle}>
                        JPG, PNG | 10MB max.
                      </p>
                    </div>
                  )}
                </div>
                <div className={classes.rightpicker}>
                  {selectedFile?.name ? (
                    <div
                      className={classes.uploadbutton}
                      onClick={handleRemoveFile}
                    >
                      <Icon icon="zondicons:minus-solid" /> Remove File
                    </div>
                  ) : null}
                </div>
              </div>
            </Grid>
            <div className={`${classes.buttons}`}>
              <div className="flex gap-10 flex-wrap-items-426">
                <Button
                  variant="primaryoutline"
                  buttonSize="full"
                  color="btnfontprimary"
                  buttonName="Submit"
                  isLoading={isLoading}
                  onClick={handleCreteTicket}
                />
                <Button
                  variant="primary"
                  buttonSize="full"
                  color="btndefault"
                  //   onClick={handleConfirmCreateModal}
                  buttonName="Cancel"
                />
              </div>
            </div>
          </div>
          {/* <ToastContainer /> */}
        </>
      }
    />
  );
};

export default CreateTicket;
